/**
 * The apply-loop review page (PIVOT-110, PIVOT-111): edit a freshly drafted
 * CV or cover letter before saving it, or view/download one already saved.
 * One page for both `ArtifactKind`s -- `reviewTitleKey` is the only place
 * the two diverge in copy; everything else (edit, save, download, the two
 * landing modes below) is identical regardless of which action produced the
 * draft.
 *
 * Two ways to land here, and the difference is the whole point of the
 * ticket's human-review gate:
 *
 * - `/apply/review`, right after `MatchCard`'s "Tailor CV" or "Write cover
 *   letter" call — the draft arrives as router state (`ApplyReviewState`,
 *   which carries `kind`) and is copied to local storage until save or
 *   cancel. The server has no unsaved copy, so this 24-hour browser copy
 *   lets a hard refresh keep the human-review gate intact. Its key uses the
 *   server-confirmed user id, so a later account cannot restore it.
 * - `/apply/:id`, after a save — the artifact is fetched by id via
 *   `GET /api/apply/artifacts` (there is no single-artifact GET route; the
 *   list is small and per-user, so filtering client-side is the whole
 *   contract). This is what makes a hard refresh safe: the content comes
 *   back from the server, never from memory -- `kind` included, so this
 *   mode never needs the router state at all.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { downloadArtifact, getArtifacts, saveArtifact } from '../api/apply'
import { useAuth } from '../auth/AuthContext'
import type { ApplyReviewState } from '../components/MatchCard'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import type { ApplyArtifact, ArtifactKind } from '../types/domain'
import './ApplyReview.css'

const DRAFT_STORAGE_PREFIX = 'pivot.applyDraft.'
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000

interface StoredDraft {
  updatedAt: number
  value: ApplyReviewState
}

/** Browser storage can be absent, corrupted, or edited by the user. Reject
 * an incomplete value instead of sending guessed fields to the save route. */
function isApplyReviewState(value: unknown): value is ApplyReviewState {
  if (typeof value !== 'object' || value === null) return false
  const state = value as Record<string, unknown>
  return (
    typeof state.jobId === 'string' &&
    typeof state.jobTitle === 'string' &&
    typeof state.jobOrg === 'string' &&
    typeof state.draft === 'string' &&
    (state.language === 'en' || state.language === 'fr' || state.language === 'nl') &&
    (state.kind === 'cv' || state.kind === 'letter')
  )
}

function draftStorageKey(userId: string): string {
  return `${DRAFT_STORAGE_PREFIX}${encodeURIComponent(userId)}`
}

function isStoredDraft(value: unknown): value is StoredDraft {
  if (typeof value !== 'object' || value === null) return false
  const stored = value as Record<string, unknown>
  return (
    typeof stored.updatedAt === 'number' &&
    Number.isFinite(stored.updatedAt) &&
    isApplyReviewState(stored.value)
  )
}

function readStoredDraft(userId: string): ApplyReviewState | null {
  const key = draftStorageKey(userId)
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    if (isStoredDraft(value)) {
      const age = Date.now() - value.updatedAt
      if (age >= 0 && age <= DRAFT_MAX_AGE_MS) return value.value
    }
    window.localStorage.removeItem(key)
  } catch {
    // Storage is best-effort. Router state still supports the current visit.
  }
  return null
}

function storeDraft(userId: string, draft: ApplyReviewState): void {
  try {
    const stored: StoredDraft = { updatedAt: Date.now(), value: draft }
    window.localStorage.setItem(draftStorageKey(userId), JSON.stringify(stored))
  } catch {
    // Storage is best-effort. React state still protects the current edit.
  }
}

function clearStoredDraft(userId: string): void {
  try {
    window.localStorage.removeItem(draftStorageKey(userId))
  } catch {
    // A blocked storage write cannot invalidate the saved server artifact.
  }
}

/** Triggers a browser download of `content` without a page navigation --
 * the fetch needs the session's Authorization header, so a plain
 * `<a href>` to the download route (no header) cannot be used. */
function downloadAsFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/markdown' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/** The one copy difference between the two kinds this page renders (module
 * docstring) -- everything else on the page is kind-neutral wording. */
function reviewTitleKey(kind: ArtifactKind): TranslationKey {
  return kind === 'letter' ? 'apply.reviewTitleLetter' : 'apply.reviewTitleCv'
}

export default function ApplyReview() {
  const { id } = useParams<{ id?: string }>()
  const { user } = useAuth()
  const userId = user?.id ?? null
  // `key` forces a remount when navigating between the two modes (draft ->
  // saved after a save, or straight to a saved id): `useApiResource` fetches
  // once on mount and does not re-run on its own when `id` changes later,
  // so without this the saved view would keep showing the draft-mode fetch's
  // stale `null` result instead of ever calling `getArtifacts` for the id
  // now in the URL.
  return <ApplyReviewForMode key={`${userId ?? 'no-user'}:${id ?? 'draft'}`} id={id} userId={userId} />
}

function ApplyReviewForMode({ id, userId }: { id?: string; userId: string | null }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useI18n()

  const draftState = useMemo<ApplyReviewState | null>(() => {
    if (id || !userId) return null
    return (location.state as ApplyReviewState | null) ?? readStoredDraft(userId)
  }, [id, location.state, userId])
  const [content, setContent] = useState(draftState?.draft ?? '')
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [downloadFailed, setDownloadFailed] = useState(false)

  const fetchSaved = useCallback(
    async (signal: AbortSignal): Promise<ApplyArtifact | null> => {
      if (!id) return null
      const artifacts = await getArtifacts(signal)
      return artifacts.find((a) => a.id === id) ?? null
    },
    [id],
  )
  const { data: saved, loading, failed, reload } = useApiResource(fetchSaved)

  useEffect(() => {
    if (draftState && userId) storeDraft(userId, { ...draftState, draft: content })
  }, [content, draftState, userId])

  // One title key per kind, resolved from whichever side of the page has it
  // (`draftState.kind` before a save, `saved.kind` after) -- the loading/
  // error branches have neither yet, so they fall back to the CV wording
  // rather than showing nothing; that copy is only on screen for the instant
  // the fetch is in flight.
  const titleKey = reviewTitleKey(draftState?.kind ?? saved?.kind ?? 'cv')
  // One tab title for every branch below (loading/error/draft/saved all reach
  // the same page — PIVOT-137). A `<title>` element, not a plain string, so
  // it can be spread into whichever branch renders without duplicating the
  // `t()` calls at each of the five return points.
  const pageTitle = <title>{`${t(titleKey)} — ${t('common.appName')}`}</title>

  const handleSave = async () => {
    if (!draftState || !userId || saving) return
    setSaving(true)
    setSaveFailed(false)
    try {
      const artifact = await saveArtifact({
        job_id: draftState.jobId,
        kind: draftState.kind,
        content,
        language: draftState.language,
      })
      clearStoredDraft(userId)
      // Replace, not push: the draft this page was showing no longer exists
      // anywhere but the server's saved copy, so a back navigation should
      // not return to a stale, now-meaningless review state.
      navigate(`/apply/${artifact.id}`, { replace: true })
    } catch {
      setSaveFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const handleDownload = async () => {
    if (!id || !saved) return
    setDownloadFailed(false)
    try {
      const text = await downloadArtifact(id)
      downloadAsFile(text, `${saved.kind}.md`)
    } catch {
      setDownloadFailed(true)
    }
  }

  const handleAbandon = () => {
    if (userId) clearStoredDraft(userId)
  }

  if (id) {
    if (loading)
      return (
        <>
          {pageTitle}
          <p role="status">{t('common.loading')}</p>
        </>
      )
    if (failed || !saved) {
      return (
        <div className="apply-review-page stack">
          {pageTitle}
          <p className="form-error" role="alert">
            {t('common.error')}
          </p>
          <button type="button" className="btn btn-ghost" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </div>
      )
    }
    return (
      <div className="apply-review-page stack">
        {pageTitle}
        <h1>{t(titleKey)}</h1>
        <p className="apply-review-job">
          {saved.job_title} · {saved.job_org}
        </p>
        <pre className="apply-review-content">{saved.content}</pre>
        <div className="apply-review-actions">
          <button type="button" className="btn btn-primary" onClick={() => void handleDownload()}>
            {t('apply.download')}
          </button>
          <Link className="btn btn-ghost" to="/dashboard">
            {t('apply.backToDashboard')}
          </Link>
        </div>
        {downloadFailed && <p className="form-error">{t('common.error')}</p>}
      </div>
    )
  }

  if (!draftState) {
    return (
      <div className="apply-review-page stack">
        {pageTitle}
        <p>{t('apply.noDraft')}</p>
        <Link className="btn btn-ghost" to="/dashboard">
          {t('apply.backToDashboard')}
        </Link>
      </div>
    )
  }

  return (
    <div className="apply-review-page stack">
      {pageTitle}
      <h1>{t(titleKey)}</h1>
      <p className="apply-review-job">
        {draftState.jobTitle} · {draftState.jobOrg}
      </p>
      <p>{t('apply.reviewHint')}</p>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={20}
        aria-label={t('a11y.applyReviewTextarea')}
      />
      <div className="apply-review-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? t('common.loading') : t('apply.save')}
        </button>
        <Link className="btn btn-ghost" to="/dashboard" onClick={handleAbandon}>
          {t('common.cancel')}
        </Link>
      </div>
      {saveFailed && <p className="form-error">{t('common.error')}</p>}
    </div>
  )
}
